/**
 * Local Wan Video Generation Constants — Phase 4 (Frontend)
 *
 * Mirror of constants/video_generation/wan_local.py (WAN_VARIANT_DEFAULTS,
 * WAN_BASE_SECONDS_PER_STEP_PER_FRAME). Keep in sync with the Python source —
 * the timing coefficients are pinned to the backend values by
 * tests/test_constants/test_wan_timing_fe_be_sync.py (fails on drift).
 */

// -------------------------------------------------------------------------
// Type aliases
// -------------------------------------------------------------------------

export type WanVariant = 'wan21_1_3b' | 'wan22_5b' | 'wan22_14b';

export interface WanLocalConfig {
  variant: WanVariant;
  steps: number;
  resolution: string; // "WxH" e.g. "832x480"
  fps: number;
  num_frames: number;
  guidance_scale: number;
  seed: number | null;
}

// -------------------------------------------------------------------------
// Per-variant specification (mirrors WanVariantSpec in wan_local.py)
// -------------------------------------------------------------------------

export interface WanVariantSpec {
  displayName: string;
  /** Minimum VRAM in GB required (NO CPU offload policy). */
  minVramGb: number;
  stepsRange: [number, number];
  defaultSteps: number;
  /** Supported resolutions as "WxH" strings. */
  supportedResolutions: string[];
  defaultResolution: string;
  fpsRange: [number, number];
  defaultFps: number;
  maxFrames: number;
  guidanceRange: [number, number];
  defaultGuidance: number;
  supportsI2v: boolean;
}

/**
 * Frontend mirror of WAN_VARIANT_DEFAULTS from constants/video_generation/wan_local.py.
 * Keep in sync with the Python source. Values are cross-checked against
 * WanVariantSpec dataclass fields.
 */
export const WAN_VARIANT_SPECS: Record<WanVariant, WanVariantSpec> = {
  wan21_1_3b: {
    displayName: 'Wan 2.1 1.3B',
    minVramGb: 8.0,
    stepsRange: [20, 50],
    defaultSteps: 30,
    supportedResolutions: ['832x480', '480x832'],
    defaultResolution: '832x480',
    fpsRange: [8, 24],
    defaultFps: 16,
    maxFrames: 81,
    guidanceRange: [1.0, 15.0],
    defaultGuidance: 7.5,
    supportsI2v: false,
  },
  wan22_5b: {
    displayName: 'Wan 2.2 5B',
    minVramGb: 12.0,
    stepsRange: [30, 60],
    defaultSteps: 50,
    supportedResolutions: ['1280x720', '720x1280', '832x480'],
    defaultResolution: '1280x720',
    fpsRange: [16, 24],
    defaultFps: 24,
    maxFrames: 121,
    guidanceRange: [1.0, 15.0],
    defaultGuidance: 7.5,
    supportsI2v: true,
  },
  wan22_14b: {
    displayName: 'Wan 2.2 14B',
    minVramGb: 20.0,
    stepsRange: [4, 8],
    defaultSteps: 4,
    supportedResolutions: ['832x480', '1280x720'],
    defaultResolution: '832x480',
    fpsRange: [16, 24],
    defaultFps: 24,
    maxFrames: 121,
    guidanceRange: [1.0, 5.0],
    defaultGuidance: 1.0,
    supportsI2v: false,
  },
};

// -------------------------------------------------------------------------
// Default config factory
// -------------------------------------------------------------------------

export function makeDefaultWanLocalConfig(variant: WanVariant): WanLocalConfig {
  const spec = WAN_VARIANT_SPECS[variant];
  return {
    variant,
    steps: spec.defaultSteps,
    resolution: spec.defaultResolution,
    fps: spec.defaultFps,
    num_frames: spec.maxFrames,
    guidance_scale: spec.defaultGuidance,
    seed: null,
  };
}

// -------------------------------------------------------------------------
// Timing estimator — mirrors estimate_wan_local_seconds() in wan_local.py
// -------------------------------------------------------------------------

/**
 * Empirical seconds per (step * frame), calibrated on RTX 3060 12GB during
 * Phase 0b (2026-05-27). Mirror of WAN_BASE_SECONDS_PER_STEP_PER_FRAME in
 * constants/video_generation/wan_local.py — pinned by
 * tests/test_constants/test_wan_timing_fe_be_sync.py.
 */
const BASE_PER_STEP_PER_FRAME: Record<WanVariant, number> = {
  wan21_1_3b: 0.25, // measured Phase 0b
  wan22_5b: 0.58, // measured Phase 0b
  wan22_14b: 1.5, // extrapolated; not calibrated (gated >= 20 GB VRAM)
};

/**
 * Estimate generation time in seconds.
 * Mirror of estimate_wan_local_seconds() in constants/video_generation/wan_local.py.
 * The vramGb parameter is reserved for future VRAM-tier adjustment.
 */
export function estimateWanSeconds(
  variant: WanVariant,
  steps: number,
  numFrames: number,
  _vramGb: number
): number {
  const coeff = BASE_PER_STEP_PER_FRAME[variant];
  return Math.round(coeff * steps * numFrames);
}
